// ═══════════════════════════════════════════════════════════════════
//  SearchBar — Global search input for the FW header.
// ═══════════════════════════════════════════════════════════════════
//
//  Phase C scaffold. Renders the input + scope chip + clear button.
//  Cmd+K / "/" focus shortcuts. Enter currently logs (Phase E will
//  navigate to /search). Live-filter wiring lands in Phase D.
//
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search as SearchIcon, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useSearch } from '@/contexts/SearchContext';
import { useTranslation } from '@/i18n/LanguageContext';
import { SearchScopeChip } from './SearchScopeChip';
import { SearchDropdown } from './SearchDropdown';

const DEBOUNCE_MS = 200;

export interface SearchBarProps {
  /** Compact mode collapses the bar into an icon button on small screens. */
  compact?: boolean;
  /** Optional className passthrough. */
  className?: string;
}

export function SearchBar({ compact = false, className }: SearchBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { query, scope, setQuery, setOpen, isOpen, reset, pushRecent } = useSearch();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [localValue, setLocalValue] = useState(query);

  // Keep local state in sync with context (e.g. when reset() is called externally).
  useEffect(() => {
    setLocalValue(query);
  }, [query]);

  // Debounced commit from local input → context. Open hålls true även
  // vid tom query (#66) — dropdownen visar då senaste sökningarna.
  // Close-vägarna är Esc, click-outside och val av träff.
  useEffect(() => {
    if (localValue === query) return;
    const handle = window.setTimeout(() => {
      setQuery(localValue);
      setOpen(true);
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [localValue, query, setQuery, setOpen]);

  // #66: click-outside stänger dropdownen. Tidigare stängde den implicit
  // när query rensades; nu när tom query visar recents behövs en explicit
  // outside-väg. Lyssnaren binds bara medan dropdownen är öppen.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (ev: PointerEvent) => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      if (ev.target instanceof Node && wrapper.contains(ev.target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isOpen, setOpen]);

  // Global keybindings: Cmd/Ctrl+K and "/"
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const target = ev.target as HTMLElement | null;
      const inField =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);
      if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === 'k') {
        ev.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      } else if (ev.key === '/' && !inField) {
        ev.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onKeyDown = useCallback(
    (ev: React.KeyboardEvent<HTMLInputElement>) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        if (localValue) {
          setLocalValue('');
          reset();
        } else {
          inputRef.current?.blur();
        }
      } else if (ev.key === 'Enter') {
        ev.preventDefault();
        const finalQuery = localValue.trim();
        if (!finalQuery) return;
        if (localValue !== query) setQuery(localValue);
        setOpen(false);
        pushRecent({ query: finalQuery, scope, hitCount: 0 });
        const params = new URLSearchParams({ q: finalQuery, scope });
        navigate(`/search?${params.toString()}`);
      }
    },
    [localValue, query, reset, setOpen, setQuery],
  );

  const onClear = useCallback(() => {
    setLocalValue('');
    reset();
    inputRef.current?.focus();
  }, [reset]);

  const hasValue = localValue.length > 0;

  return (
    <div
      ref={wrapperRef}
      className={`relative flex items-center gap-2 ${compact ? 'w-auto' : 'w-full max-w-md'} ${
        className ?? ''
      }`}
      role="search"
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="relative flex-1">
            <SearchIcon
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              ref={inputRef}
              type="text"
              value={localValue}
              placeholder={t('searchCanvasPlaceholder')}
              onChange={(ev) => setLocalValue(ev.target.value)}
              onKeyDown={onKeyDown}
              onFocus={() => {
                // #66: öppna även med tom query — recents-panelen visas då.
                setOpen(true);
              }}
              className="pl-9 pr-20"
              aria-label={t('searchCanvasPlaceholder')}
              aria-expanded={isOpen}
            />
            {hasValue && (
              <button
                type="button"
                onClick={onClear}
                aria-label="Clear search"
                className="absolute right-12 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              >
                <X size={14} />
              </button>
            )}
            <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border bg-muted px-1.5 py-0.5 text-xs font-mono text-muted-foreground">
              ⌘K
            </kbd>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start">
          <p className="max-w-xs text-xs">{t('searchCanvasTooltip')}</p>
        </TooltipContent>
      </Tooltip>
      <SearchScopeChip />
      <SearchDropdown />
    </div>
  );
}
